import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CompanyProvider } from "@/context/CompanyContext";
import AppLayout from "@/components/layout/AppLayout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ChangePassword from "@/pages/ChangePassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Companies from "@/pages/Companies";
import Employees from "@/pages/Employees";
import EmployeeProfile from "@/pages/EmployeeProfile";
import Attendance from "@/pages/Attendance";
import Leaves from "@/pages/Leaves";
import Payroll from "@/pages/Payroll";
import Recruitment from "@/pages/Recruitment";
import Documents from "@/pages/Documents";
import Performance from "@/pages/Performance";
import Announcements from "@/pages/Announcements";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Compliance from "@/pages/Compliance";

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white text-sm text-[#64748B]" data-testid="auth-loading">
        <div className="font-mono">Loading...</div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return <div className="p-10 text-center" data-testid="forbidden"><h2 className="font-display text-2xl">Forbidden</h2><p className="text-sm text-[#475569] mt-2">You don't have access to this page.</p></div>;
  }
  return children;
}

function GuestOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CompanyProvider>
            <Toaster position="top-right" richColors />
            <Routes>
              <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
              <Route path="/forgot-password" element={<GuestOnly><ForgotPassword /></GuestOnly>} />
              <Route path="/reset-password" element={<GuestOnly><ResetPassword /></GuestOnly>}/>
              <Route element={<Protected><AppLayout /></Protected>}>
                <Route index element={<Dashboard />} />
                <Route path="companies" element={<Protected roles={["super_admin", "hr_admin"]}><Companies /></Protected>} />
                <Route path="employees" element={<Protected roles={["super_admin", "hr_admin", "manager"]}><Employees /></Protected>} />
                <Route path="employees/:id" element={<EmployeeProfile />} />
                <Route path="attendance" element={<Attendance />} />
                <Route path="leaves" element={<Leaves />} />
                <Route path="payroll" element={<Protected roles={["super_admin", "hr_admin", "manager", "employee"]}><Payroll /></Protected>} />
                <Route path="recruitment" element={<Protected roles={["super_admin", "hr_admin"]}><Recruitment /></Protected>} />
                <Route path="documents" element={<Documents />} />
                <Route path="performance" element={<Performance />} />
                <Route path="announcements" element={<Announcements />} />
                <Route path="reports" element={<Protected roles={["super_admin", "hr_admin"]}><Reports /></Protected>} />
                <Route path="compliance" element={<Compliance />} />
                <Route path="settings" element={<Protected roles={["super_admin", "hr_admin"]}><Settings /></Protected>} />
                <Route path="change-password" element={<ChangePassword />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </CompanyProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
